import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ControlledSelect from "../../../../components/ControlledSelect";
import ControlledCheckBoxField from "../../../../components/ControlledCheckBoxField";
import ButtonComponent from "../../../../components/ButtonComponent";
import {
  useSaveDimensionMutation,
  useGetDimensionsQuery,
  useLazyGetMenuTypesQuery,
  ProductDimension
} from "../../../../redux/services/CustomTypeService";

interface EditDimensionFormData {
  displayName: string;
  isRequired: boolean;
  selectionOrder: { value: number; label: string };
}

const EditDimension: React.FC = () => {
  const { menuType, dimensionId } = useParams<{ menuType: string; dimensionId: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dimension, setDimension] = useState<ProductDimension | null>(null);
  const [menuTypeId, setMenuTypeId] = useState<string | null>(null);

  // Get restaurant ID from Redux
  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Get menu types to find the ID
  const [triggerMenuTypes, { data: menuTypes = [] }] = useLazyGetMenuTypesQuery();
  
  // Get dimensions data
  const { data: productDimensions = [], isLoading, refetch } = useGetDimensionsQuery(
    {
      restaurantId: selectedRestaurantId,
      customMenuItemTypeId: menuTypeId || ""
    },
    {
      skip: !selectedRestaurantId || !menuTypeId
    }
  );

  // Get the actual mutation hook
  const [saveDimensionMutation] = useSaveDimensionMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<EditDimensionFormData>();

  // Options for selection order
  const selectionOrderOptions = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  // Find the menu type ID based on the name from URL
  useEffect(() => {
    if (selectedRestaurantId && menuType) {
      triggerMenuTypes({ restaurantId: selectedRestaurantId });
    }
  }, [selectedRestaurantId, menuType, triggerMenuTypes]);

  useEffect(() => {
    if (menuTypes && menuTypes.length > 0 && menuType) {
      const foundMenuType = menuTypes.find(mt => mt.name === menuType);
      if (foundMenuType) {
        setMenuTypeId(foundMenuType.id);
      }
    }
  }, [menuTypes, menuType]);

  // Load dimension data when productDimensions is available
  useEffect(() => {
    if (productDimensions && productDimensions.length > 0 && dimensionId) {
      const foundDimension = productDimensions.find(dim => dim.id === dimensionId);
      
      if (foundDimension) {
        setDimension(foundDimension);
        reset({
          displayName: foundDimension.displayName,
          isRequired: foundDimension.isRequired,
          selectionOrder: {
            value: foundDimension.selectionOrder,
            label: foundDimension.selectionOrder.toString()
          },
        });
      } else {
        toast.error("Dimension not found");
        navigate(`/configuration/customize-menu-type/${menuType}/dimensions`);
      }
    }
  }, [productDimensions, dimensionId, reset, navigate, menuType]);

  const onSubmit = async (data: EditDimensionFormData) => {
    setIsSubmitting(true);
    
    try {
      if (!selectedRestaurantId) {
        toast.error("Restaurant not selected. Please refresh and try again.");
        return;
      }

      if (!menuTypeId) {
        toast.error("Menu type ID not found. Please navigate back and try again.");
        return;
      }

      const dimensionData = {
        restaurantId: selectedRestaurantId,
        customMenuItemTypeId: menuTypeId,
        displayName: data.displayName,
        selectionOrder: data.selectionOrder.value,
        isRequired: data.isRequired,
        dimensionId: dimensionId, 
      };
      
      await saveDimensionMutation(dimensionData).unwrap();
      
      toast.success("Dimension updated successfully!");
      
      // Refresh data and navigate back
      refetch();
      navigate(`/productCatalogue/product-groups/${menuType}/dimensions`, {
        state: { shouldRefetch: true }
      });
      
    } catch (error: any) {
      console.error("Error updating dimension:", error);
      const errorMessage = error?.data || error?.message || "Failed to update dimension. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayNameWatcher = watch("displayName");

  if (isLoading) {
    return (
      <>
        <BreadCrumbs 
          hasSubMenus={true}
          customNameMap={{
            [menuTypeId || '']: menuType || 'Menu Type'
          }}
          disabledCrumbs={['edit-dimension']}
        />
        <Card className="mt-3">
          <CardBody className="text-center py-5">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-muted" />
            <p className="mt-3 text-muted">Loading dimension data...</p>
          </CardBody>
        </Card>
      </>
    );
  }

  if (!dimension) {
    return (
      <>
        <BreadCrumbs 
          hasSubMenus={true}
          customNameMap={{
            [menuTypeId || '']: menuType || 'Menu Type'
          }}
          disabledCrumbs={['edit-dimension']}
        />
        <Card className="mt-3">
          <CardBody className="text-center py-5">
            <p className="text-danger">Dimension not found</p>
            <Button 
              variant="primary" 
              onClick={() => navigate(`/productCatalogue/product-groups/${menuType}/dimensions`)}
            >
              Back to Dimensions
            </Button>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <BreadCrumbs 
        hasSubMenus={true}
        customNameMap={{
          [menuTypeId || '']: menuType || 'Menu Type',
          [dimensionId || '']: dimension?.displayName || 'Dimension'
        }}
        disabledCrumbs={['edit-dimension']}
      />
      
      <Card className="mt-3">
        <CardHeader className="header-bg-color d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5 className="mb-0">Edit Dimension: {dimension.displayName}</h5>
          </div>
        </CardHeader>
        
        <CardBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={8}>
                <ControlledRowTextField
                  label="Display Name"
                  inputName="displayName"
                  inputType="text"
                  placeholder="e.g., Choose Your Part, Select Style"
                  control={control}
                  rules={{
                    required: "Display name is required",
                    minLength: {
                      value: 3,
                      message: "Display name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 50,
                      message: "Display name must be less than 50 characters",
                    },
                  }}
                />
                {errors.displayName && (
                  <span className="text-danger small">{errors.displayName.message}</span>
                )}
              </Col>
              
              <Col md={4}>
                <ControlledSelect
                  label="Selection Order"
                  inputName="selectionOrder"
                  control={control}
                  optionsList={selectionOrderOptions}
                  rules={{ required: "Selection order is required" }}
                />
                {errors.selectionOrder && (
                  <span className="text-danger small">{errors.selectionOrder.message}</span>
                )}
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <ControlledCheckBoxField
                  label="Required Dimension"
                  inputName="isRequired"
                  control={control}
                />
                <small className="text-muted">Check this if customers must select a value for this dimension</small>
                {errors.isRequired && (
                  <span className="text-danger small">{errors.isRequired.message}</span>
                )}
              </Col>
            </Row>

            <div className="mt-4">
              <div className="alert alert-light border">
                <h6 className="mb-2">Dimension Information:</h6>
                <Row className="small">
                  <Col md={6}>
                    <div><strong>Dimension ID:</strong> {dimension?.id}</div>
                    <div><strong>Menu Type:</strong> {menuType}</div>
                  </Col>
                  <Col md={6}>
                    <div><strong>Created:</strong> {dimension?.createdAt ? new Date(dimension.createdAt._seconds * 1000).toLocaleDateString() : 'Unknown'}</div>
                    <div><strong>Selection Order:</strong> {dimension?.selectionOrder}</div>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => navigate(`/productCatalogue/product-groups/${menuType}/dimensions`)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <ButtonComponent
                type="submit"
                isDisabled={isSubmitting || !displayNameWatcher}
                titel={isSubmitting ? "Updating..." : "Update Dimension"}
                icon={<FontAwesomeIcon icon={isSubmitting ? faSpinner : faSave} spin={isSubmitting} />}
              />
            </div>
          </Form>
        </CardBody>
      </Card>
    </>
  );
};

export default EditDimension;